import {
    Box, Button, IconButton, List, ListItem, ListItemText, Paper, TextField, Typography
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import { useAsync2 } from "nate-react-api-helpers";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../api/API";
import { ConfirmDialog } from "../../misc/ConfirmDialog";
import { useCurrentUser } from "../../misc/CurrentUser";
import { Redirect } from "../../misc/Redirect";
import { useSnackAction } from "../../misc/SnackAction";
import { useWorkspace } from "../../misc/Workspace";

// TeamSettingsPage is where a team is renamed, its members removed, people invited and
// the team archived. "All" has no team of its own, so it lists the teams instead.
export function TeamSettingsPage() {
    const ws = useWorkspace();
    const me = useCurrentUser();
    const navigate = useNavigate();
    const team = ws.current;

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [confirm, setConfirm] = useState<"leave" | "archive" | null>(null);

    useEffect(() => setName(team?.name || ""), [team?.name]);

    const members = useAsync2(input => input.team ? api.teams.listMembers(input) : Promise.resolve(null),
        { team: ws.team }, [ws.team]);

    const rename = useSnackAction(async (input: { name: string }) => {
        await api.teams.upsert({ id: ws.team, name: input.name });
        ws.reload();
    }, { success: "Saved" });

    const remove = useSnackAction(async (input: { usr: number }) => {
        await api.teams.upsertMember({ team: ws.team, usr: input.usr, archived: true });
        members.reload();
        ws.reload();
    }, { success: "Removed" });

    const leave = useSnackAction(async () => {
        await api.teams.upsertMember({ team: ws.team, usr: me?.id || 0, archived: true });
        ws.reload();
        navigate("/w/all/teams");
    }, { success: "You left the team" });

    const invite = useSnackAction(async (input: { email: string }) => {
        await api.teams.upsertInvite({ team: ws.team, email: input.email });
        setEmail("");
        members.reload();
    }, { success: "Invite sent" });

    const cancelInvite = useSnackAction(async (input: { id: number }) => {
        await api.teams.upsertInvite({ id: input.id, archived: true });
        members.reload();
    }, { success: "Invite cancelled" });

    const archive = useSnackAction(async () => {
        await api.teams.upsert({ id: ws.team, name: team?.name || "", archived: true });
        ws.reload();
        navigate("/w/all/teams");
    }, { success: "Team archived" });

    if (!ws.team) return <Redirect to={ws.path("/teams")} />;
    if (!team) return <Typography variant="body2">{ws.loading ? "Loading..." : "You aren't on that team"}</Typography>;

    const list = members.result?.members || [];
    const invites = members.result?.invites || [];
    const busy = remove.loading || leave.loading || invite.loading || cancelInvite.loading || archive.loading;

    return (
        <Box sx={{ maxWidth: 640, display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h5">{team.personal ? "Personal" : team.name}</Typography>

            {team.personal && (
                <Typography variant="body2" color="text.secondary">
                    Your personal team is only for you. Make a new team to work with other people.
                </Typography>
            )}

            {!team.personal && (
                <Paper sx={{ p: 2 }}>
                    <form style={{ display: "flex", gap: 8 }} onSubmit={e => {
                        e.preventDefault();
                        if (name.trim()) rename.callback({ name: name.trim() });
                    }}>
                        <TextField size="small" label="Name" fullWidth value={name}
                                   onChange={e => setName(e.target.value)} />
                        <Button type="submit" disabled={rename.loading || !name.trim() || name.trim() === team.name}>
                            Rename
                        </Button>
                    </form>
                </Paper>
            )}

            <Paper>
                <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5 }}>Members</Typography>
                {members.LoadingOrErrorElement}
                <List dense>
                    {list.map(member => (
                        <ListItem key={member.usr} secondaryAction={!team.personal && list.length > 1 && (
                            member.usr === me?.id ? (
                                <Button size="small" disabled={busy} onClick={() => setConfirm("leave")}>
                                    Leave team
                                </Button>
                            ) : (
                                <Button size="small" color="error" disabled={busy}
                                        onClick={() => remove.callback({ usr: member.usr })}>
                                    Remove
                                </Button>
                            )
                        )}>
                            <ListItemText primary={member.name} secondary={member.email} />
                        </ListItem>
                    ))}
                </List>
            </Paper>

            {!team.personal && (
                <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ pb: 1 }}>Invite</Typography>
                    <form style={{ display: "flex", gap: 8 }} onSubmit={e => {
                        e.preventDefault();
                        if (email.trim()) invite.callback({ email: email.trim() });
                    }}>
                        <TextField size="small" label="Email" fullWidth value={email}
                                   slotProps={{ htmlInput: { inputMode: "email", autoCapitalize: "none" } }}
                                   onChange={e => setEmail(e.target.value)} />
                        <Button type="submit" variant="contained" disabled={busy || !email.trim()}>Send</Button>
                    </form>

                    {invites.length > 0 && (
                        <List dense>
                            {invites.map(item => (
                                <ListItem key={item.id} secondaryAction={
                                    <IconButton size="small" title="Cancel invite" disabled={busy}
                                                onClick={() => cancelInvite.callback({ id: item.id })}>
                                        <Close fontSize="small" />
                                    </IconButton>
                                }>
                                    <ListItemText primary={item.email} secondary="Invite sent" />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Paper>
            )}

            {!team.personal && (
                <Box>
                    <Button color="error" variant="outlined" disabled={busy} onClick={() => setConfirm("archive")}>
                        Archive team
                    </Button>
                </Box>
            )}

            <ConfirmDialog
                open={confirm === "leave"}
                title="Leave team?"
                message={"You won't see the tasks and projects of " + team.name + " any more. Your tasks on it go to other members."}
                confirmLabel="Leave"
                confirmColor="error"
                onCancel={() => setConfirm(null)}
                onConfirm={() => {
                    setConfirm(null);
                    leave.callback(null);
                }}
            />

            <ConfirmDialog
                open={confirm === "archive"}
                title="Archive team?"
                message={"Nobody will see the tasks and projects of " + team.name + " any more."}
                confirmLabel="Archive"
                confirmColor="error"
                onCancel={() => setConfirm(null)}
                onConfirm={() => {
                    setConfirm(null);
                    archive.callback(null);
                }}
            />
        </Box>
    );
}
